import React from "react";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import ContactCTA from "../components/ContactCTA";
import FaqSection from "../components/FaqSection";
import { PhosphorIcon } from "../components/PhosphorIcon";
import { CONTACT, canonicalFor } from "../config/site";
import useReveal from "../hooks/useReveal";
import "../styles/local-page.css";

const SERVICES = [
  { icon: "Database", title: "ERP implementation", body: "Oracle, Zoho and Odoo implementation and support for businesses across Chhattisgarh and India." },
  { icon: "Receipt", title: "E-invoicing & GST", body: "GST e-invoicing, IRN, e-way bill and 2A/2B reconciliation integrated with your accounting system." },
  { icon: "CodeBlock", title: "Software development", body: "Custom web and integration software, dashboards and automation built by a local engineering team." },
  { icon: "UsersThree", title: "IT staffing", body: "Oracle, SAP and e-invoicing specialists for projects across India, UAE, KSA and the US." },
];

const RAIPUR_FAQS = [
  {
    question: "Which ERP services do you offer from Raipur?",
    answer:
      "From our Raipur office we implement and support Oracle (Fusion and EBS), Zoho and Odoo, covering implementation, customization, integration and ongoing support for businesses in Chhattisgarh and across India.",
  },
  {
    question: "Can you set up GST e-invoicing for a business in Chhattisgarh?",
    answer:
      "Yes. We implement GST e-invoicing — IRN generation, e-way bill and 2A/2B reconciliation — and integrate it directly into your accounting or ERP system (Zoho, Odoo, Oracle or Tally), so compliance happens inside the tools your team already uses.",
  },
  {
    question: "Do you implement Zoho and Odoo for small and mid-size businesses in Raipur?",
    answer:
      "Yes. Zoho and Odoo are well suited to growing businesses. We handle edition selection, implementation, migration from Tally or spreadsheets, and GST-compliant invoicing — with a local team you can meet in Raipur.",
  },
  {
    question: "Do you only work with clients in Raipur?",
    answer:
      "No. Raipur is our base, but we deliver ERP and e-invoicing projects across India and internationally — including the UAE, Saudi Arabia, the US and Canada. Local clients get the benefit of an on-the-ground team plus global delivery experience.",
  },
  {
    question: "How do I get started?",
    answer:
      "Book a free consultation or email hello@cloudare.in. We'll discuss your current systems, compliance needs and goals, then propose a right-sized approach.",
  },
];

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Cloudare Technologies",
  description:
    "Oracle-certified ERP and e-invoicing company in Raipur, Chhattisgarh, serving clients across India and globally.",
  url: "https://www.cloudare.in/raipur",
  email: "hello@cloudare.in",
  address: {
    "@type": "PostalAddress",
    streetAddress: "7th Floor, Currency Tower, VIP Road, Telibandha",
    addressLocality: "Raipur",
    addressRegion: "Chhattisgarh",
    postalCode: "492001",
    addressCountry: "IN",
  },
  areaServed: ["Raipur", "Chhattisgarh", "India"],
  knowsAbout: [
    "Oracle ERP", "Oracle Fusion", "Oracle EBS", "Zoho", "Odoo",
    "GST e-invoicing", "ERP implementation", "IT staffing", "software development",
  ],
};

export default function RaipurPage() {
  useReveal();
  return (
    <React.Fragment>
      <Seo
        path="/raipur"
        title="IT & ERP Company in Raipur, Chhattisgarh | Cloudare"
        description="Cloudare is an Oracle-certified ERP, e-invoicing and software company in Raipur, Chhattisgarh. ERP implementation, GST e-invoicing, custom software and IT staffing — local team, global delivery."
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>
      <Navbar />

      <section className="lp-hero reveal">
        <div className="lp-hero__inner">
          <p className="lp-eyebrow">Raipur · Chhattisgarh</p>
          <h1 className="lp-hero__title">
            An IT &amp; ERP company in Raipur, building for the world
          </h1>
          <p className="lp-hero__subhead">
            Cloudare is an Oracle-certified ERP and e-invoicing firm headquartered
            in Raipur, Chhattisgarh — delivering ERP implementation, GST
            e-invoicing, custom software and IT staffing for clients across India,
            the UAE, Saudi Arabia and the US.
          </p>
          <div className="lp-hero__cta">
            <a className="btn-primary" href={CONTACT.calendly} target="_blank" rel="noopener noreferrer">
              Talk to our Raipur team
            </a>
            <NavLink className="btn-secondary" to="/contact-us">Contact us</NavLink>
          </div>
        </div>
      </section>

      <section className="lp-section">
        <header className="why-cloudare__header reveal">
          <p className="why-cloudare__eyebrow">What we do locally</p>
          <h2 className="why-cloudare__title">Services from our Raipur office</h2>
        </header>
        <div className="lp-grid reveal-stagger">
          {SERVICES.map((s) => (
            <article className="lp-card" key={s.title}>
              <div className="lp-card__icon">
                <PhosphorIcon name={s.icon} size={24} />
              </div>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="lp-section lp-section--alt">
        <div className="lp-address reveal">
          <div className="lp-address__icon">
            <PhosphorIcon name="MapPin" size={28} />
          </div>
          <div>
            <h2>Our Raipur office</h2>
            <p>
              7th Floor, Currency Tower, VIP Road, Telibandha<br />
              Raipur, Chhattisgarh – 492001, India
            </p>
            <p className="lp-address__contact">
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
            </p>
          </div>
        </div>
      </section>

      <div id="faq">
        <FaqSection
          eyebrow="Frequently asked questions"
          title="ERP & e-invoicing in Raipur — FAQ"
          faqs={RAIPUR_FAQS}
          emitJsonLd
        />
      </div>

      <ContactCTA
        title="Looking for an ERP or software partner in Chhattisgarh?"
        subtitle="Local team, enterprise-grade delivery. Let's talk about your project."
        primaryLabel="Book a free consultation"
      />
      <Footer />
    </React.Fragment>
  );
}
